// src/components/organisms/PatientWorkspaceTabs/PrescriptionEditor.js
//
// Reçete editörü. Aynı Treatment kaydına yazar ama yalnız medications,
// prescriptionNote ve prescriptionRequested alanlarını günceller.
// buildPayload, treatment snapshot'ını okuyup edit alanlarıyla birleştirir
// (server full-form bekliyor; partial PATCH yok).
// Düz editör dili (kutusuz bölümler, drawer ile ekleme): bkz. _editorParts.

import React, { Suspense, lazy, useCallback, useEffect, useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import {
  LocalPharmacy as LocalPharmacyIcon,
  Alarm as AlarmIcon,
} from "@mui/icons-material";

import ActionButton from "../../atoms/ActionButton";
import FormTextField from "../../atoms/FormTextField";
import { useClinicTheme } from "../../../theme";
import { COLORS, TYPO } from "../../../styles/uiConstants";
import useInlineAutosave from "../../../hooks/useInlineAutosave";
import { useHasPermission } from "../../../hooks/useHasPermission";
import PrescriptionAlarmSuggestDialog from "./PrescriptionAlarmSuggestDialog";
import MedicationPickerDrawer from "./drawers/MedicationPickerDrawer";
import { makeFetchPage, pickMedicines, medicineOption } from "./drawers/fetchers";
import {
  EDITOR_PX,
  SECTION_SX,
  listAreaSx,
  FieldRow,
  SectionHeader,
  SectionEmpty,
  EditorHeader,
  PdfButton,
} from "./_editorParts";
import {
  getRefId,
  EMPTY_MEDICINE_FORM,
  medicineFormFromTemplate,
  medicineFormFromEntry,
  medicineEntryFromForm,
  canSubmitMedicineForm,
  MedicationRows,
} from "./_itemLists";

import { useUpdateTreatmentMutation } from "../../../store/secretaryApi";
import { useListMedicineTemplatesMutation } from "../../../store/systemVariablesApi";

// Bkz. TreatmentEditor: @react-pdf ayrı chunk'ta kalsın diye tembel yüklenir.
const PrescriptionPreview = lazy(() => import("../pdf/PrescriptionPreview"));

// İlaç listesi sabit yükseklikte (bkz. _editorParts)
const MED_LIST_HEIGHT = 312;

const prescriptionFieldsFromRecord = (record) => ({
  medications: record?.medications || [],
  prescriptionNote: record?.prescriptionNote || "",
  prescriptionRequested: Boolean(record?.prescriptionRequested),
});

// Mevcut treatment snapshot'ını + edit alanlarını birleştirip full payload üretir.
const buildPrescriptionPayload = (form, record) => {
  const rawAllowed = record?.allowedFoods || record?.dietInfo?.allowedList || [];
  const rawForbidden =
    record?.forbiddenFoods || record?.dietInfo?.forbiddenList || [];
  return {
    diagnoses: record?.diagnoses || [],
    complaints: (record?.complaints || []).filter(
      (c) => typeof c === "string" && c.trim()
    ),
    // Gönderilmezse sunucuda dokunulmaz
    specialNotes: record?.specialNotes || undefined,
    medications: (form.medications || []).map((m) => ({
      ...m,
      medicine: getRefId(m.medicine),
      isTaken: Boolean(m.isTaken),
    })),
    // Boşaltılınca "" gönderilir ki sunucuda da silinsin
    prescriptionNote: form.prescriptionNote ?? "",
    dietNote: record?.dietNote || undefined,
    prescriptionRequested:
      (form.medications || []).length > 0
        ? false
        : Boolean(form.prescriptionRequested),
    dietRequested: Boolean(record?.dietRequested),
    allowedFoods: rawAllowed.map((f) => ({
      food: getRefId(f.food || f._id || f),
      name: f.name || (f.food && f.food.name) || "",
      amount: f.amount,
      amountUnit: f.amountUnit,
      notes: f.notes,
    })),
    forbiddenFoods: rawForbidden.map((f) => ({
      food: getRefId(f.food || f._id || f),
      name: f.name || (f.food && f.food.name) || "",
      amount: f.amount,
      amountUnit: f.amountUnit,
      notes: f.notes,
    })),
    nextControlDate: record?.nextControlDate || undefined,
    nextControlNote: record?.nextControlNote || undefined,
  };
};

export default function PrescriptionEditor({
  record,
  patient,
  patientId,
  token,
  examinationId,
  canEdit = true,
  onSaved,
  onError,
  registerAutoSave,
  registerFlush,
  onSaveStatusChange,
}) {
  const { palette } = useClinicTheme();

  const [updateTreatment] = useUpdateTreatmentMutation();
  const [listMedicineTemplates] = useListMedicineTemplatesMutation();
  const [pdfOpen, setPdfOpen] = useState(false);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const canManagePlans = useHasPermission("medication-plans:manage");

  const updateFn = useCallback(
    async (payload) => {
      const res = await updateTreatment(payload).unwrap();
      return res?.data?.treatment || res?.treatment || res;
    },
    [updateTreatment]
  );

  const { fieldValues, setField, saveStatus } = useInlineAutosave({
    record,
    createMutation: async () => {
      throw new Error("PrescriptionEditor does not support create.");
    },
    updateMutation: updateFn,
    fieldsFromRecord: prescriptionFieldsFromRecord,
    shouldCreate: () => false,
    debounceMs: 500,
    manualCreate: false,
    buildPayload: (form) => ({
      token,
      patientId,
      ...(examinationId ? { examinationId } : {}),
      ...buildPrescriptionPayload(form, record),
    }),
    recordIdField: "treatmentId",
    revertOnError: true,
    onSaved,
    onError,
    registerAutoSave,
    registerFlush,
  });

  useEffect(() => {
    if (typeof onSaveStatusChange === "function") onSaveStatusChange(saveStatus);
  }, [saveStatus, onSaveStatusChange]);

  // ── İlaç drawer'ı: ekleme (editingIdx null) ve düzenleme ──
  const [medicineForm, setMedicineForm] = useState(EMPTY_MEDICINE_FORM);
  const [editingIdx, setEditingIdx] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    setMedicineForm(EMPTY_MEDICINE_FORM);
    setEditingIdx(null);
    setDrawerOpen(false);
  }, [record?._id]);

  const fetchMedicines = useMemo(
    () => makeFetchPage(listMedicineTemplates, token, pickMedicines, medicineOption),
    [listMedicineTemplates, token]
  );

  const medications = useMemo(() => fieldValues.medications || [], [fieldValues.medications]);

  const openAdd = () => {
    setEditingIdx(null);
    setMedicineForm(EMPTY_MEDICINE_FORM);
    setDrawerOpen(true);
  };
  const openEdit = (idx) => {
    const m = medications[idx];
    if (!m) return;
    setEditingIdx(idx);
    setMedicineForm(medicineFormFromEntry(m));
    setDrawerOpen(true);
  };

  const submitMedicine = () => {
    if (editingIdx == null) {
      const entry = medicineEntryFromForm(medicineForm);
      if (!entry) return;
      if (medications.some((x) => String(getRefId(x.medicine)) === String(entry.medicine))) return;
      setField("medications", [...medications, entry]);
    } else {
      const entry = medicineEntryFromForm(medicineForm, medications[editingIdx]);
      if (!entry) return;
      setField("medications", medications.map((m, i) => (i === editingIdx ? entry : m)));
    }
    setDrawerOpen(false);
  };

  const removeMedication = (idx) => {
    setField("medications", medications.filter((_, i) => i !== idx));
  };

  // Drawer'da "Ekli" işareti — düzenlenen ilacın kendisi hariç
  const existingIds = useMemo(
    () =>
      new Set(
        medications
          .filter((_, i) => i !== editingIdx)
          .map((m) => String(getRefId(m.medicine)))
      ),
    [medications, editingIdx]
  );

  const showPrescriptionToggle = canEdit && medications.length === 0;

  const contextDiagnoses = useMemo(
    () =>
      (record?.diagnoses || [])
        .map((d) => d.diagnosisName || d.diagnosisCategoryName)
        .filter(Boolean)
        .join(", ") || "—",
    [record]
  );
  const contextComplaints = useMemo(
    () => (record?.complaints || []).join(", ") || "—",
    [record]
  );

  const contextText = (text) => (
    <Typography sx={{ fontSize: TYPO.body, color: COLORS.slate700, pt: 0.5 }}>{text}</Typography>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      <EditorHeader
        record={record}
        newLabel="Reçete"
        editLabel="Reçete düzenleniyor"
        saveStatus={saveStatus}
        palette={palette}
        actions={
          <>
            {canEdit && canManagePlans && (record?.medications || []).length > 0 ? (
              <ActionButton
                variant="add"
                tone="outline"
                compact
                label="Alarm planı oluştur"
                icon={<AlarmIcon sx={{ fontSize: 16 }} />}
                onClick={() => setSuggestOpen(true)}
              />
            ) : null}
            {/* Çıktı — salt-okunur kullanıcı da alabilmeli */}
            {record?._id ? (
              <PdfButton
                title="Reçete çıktısı (PDF)"
                onClick={() => setPdfOpen(true)}
                palette={palette}
              />
            ) : null}
          </>
        }
      />

      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
        {/* Reçete bağlamı (tedaviden, salt okunur) */}
        <Box sx={SECTION_SX}>
          <FieldRow label="Tanılar">{contextText(contextDiagnoses)}</FieldRow>
          <FieldRow label="Şikayetler">{contextText(contextComplaints)}</FieldRow>
        </Box>

        {/* İlaçlar */}
        <Box sx={SECTION_SX}>
          <SectionHeader
            title="İlaçlar"
            count={medications.length}
            addLabel="İlaç Ekle"
            onAdd={canEdit ? openAdd : undefined}
            palette={palette}
          />
          <Box sx={listAreaSx(MED_LIST_HEIGHT, medications.length === 0)}>
            {medications.length === 0 ? (
              <SectionEmpty
                icon={<LocalPharmacyIcon />}
                title="Henüz ilaç eklenmedi"
                hint={canEdit ? "Reçeteye ilaç eklemek için İlaç Ekle'yi kullanın." : undefined}
              />
            ) : (
              <MedicationRows
                items={medications}
                palette={palette}
                onEdit={canEdit ? openEdit : undefined}
                onRemove={canEdit ? removeMedication : undefined}
              />
            )}
          </Box>
        </Box>

        {/* Reçete Notu */}
        <Box sx={{ ...SECTION_SX, borderBottom: "none" }}>
          <FieldRow label="Reçete Notu">
            <FormTextField
              palette={palette}
              variant="standard"
              size="small"
              fullWidth
              multiline
              minRows={3}
              placeholder="Reçete ile ilgili notlar"
              value={fieldValues.prescriptionNote || ""}
              onChange={(e) => setField("prescriptionNote", e.target.value)}
              disabled={!canEdit}
            />
          </FieldRow>
        </Box>
      </Box>

      {/* Alt çubuk — yalnız henüz ilaç yokken reçete talebi aç/kaldır */}
      {showPrescriptionToggle && (
        <Box
          sx={{
            flexShrink: 0,
            ...EDITOR_PX,
            py: 1.5,
            borderTop: `1px solid ${COLORS.slate100}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1.5,
          }}
        >
          <Typography sx={{ fontSize: TYPO.label, color: COLORS.slate500 }}>
            {fieldValues.prescriptionRequested
              ? "Bu tedavi için reçete talebi açık."
              : "İlaç yazılmadıysa reçete talebi açabilirsiniz."}
          </Typography>
          <ActionButton
            variant={fieldValues.prescriptionRequested ? "delete" : "confirm"}
            palette={palette}
            compact
            onClick={() => setField("prescriptionRequested", !fieldValues.prescriptionRequested)}
            label={fieldValues.prescriptionRequested ? "Reçete Talebini Kaldır" : "Reçete Talebi Aç"}
          />
        </Box>
      )}

      <MedicationPickerDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        fetchPage={fetchMedicines}
        existingIds={existingIds}
        form={medicineForm}
        onSelectMedicine={(v) => setMedicineForm(medicineFormFromTemplate(v))}
        onFormChange={(patch) => setMedicineForm((p) => ({ ...p, ...patch }))}
        onSubmit={submitMedicine}
        canSubmit={canSubmitMedicineForm(medicineForm)}
        title={editingIdx == null ? "İlaç Ekle" : "İlacı Düzenle"}
        submitLabel={editingIdx == null ? "İlaç Ekle" : "Kaydet"}
        editing={editingIdx != null}
      />

      {suggestOpen && record?._id ? (
        <PrescriptionAlarmSuggestDialog
          open={suggestOpen}
          onClose={() => setSuggestOpen(false)}
          patientId={patientId || patient?._id}
          treatmentId={record._id}
          token={token}
        />
      ) : null}

      {pdfOpen && (
        <Suspense fallback={null}>
          <PrescriptionPreview
            open={pdfOpen}
            onClose={() => setPdfOpen(false)}
            patient={patient}
            treatment={{ ...(record || {}), ...fieldValues }}
          />
        </Suspense>
      )}
    </Box>
  );
}
